import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { VisualLogger } from "@/components/debug/VisualDebugger";

const VISUAL_DEBUGGER_STORAGE_KEY = "debug.visualDebuggerEnabled";

const isEnvVisualDebuggerEnabled =
  ((import.meta as any).env?.VITE_VISUAL_DEBUGGER === "true") ||
  (typeof process !== "undefined" && process.env?.VITE_VISUAL_DEBUGGER === "true");

const readStoredPreference = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const storedValue = window.sessionStorage.getItem(
      VISUAL_DEBUGGER_STORAGE_KEY
    );
    return storedValue === null ? null : storedValue === "true";
  } catch (error) {
    console.warn("Impossibile leggere la preferenza del visual debugger", error);
    return null;
  }
};

const persistPreference = (value: boolean) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      VISUAL_DEBUGGER_STORAGE_KEY,
      value ? "true" : "false"
    );
  } catch (error) {
    console.warn("Impossibile salvare la preferenza del visual debugger", error);
  }
};

export const useDebugPreferences = () => {
  const location = useLocation();
  const queryOverrideActive = useMemo(() => {
    return new URLSearchParams(location.search).get("debug") === "keyboard";
  }, [location.search]);

  const [visualDebuggerPreference, setVisualDebuggerPreference] = useState(() => {
    const storedValue = readStoredPreference();

    if (storedValue !== null) {
      return storedValue;
    }

    if (queryOverrideActive || isEnvVisualDebuggerEnabled) {
      persistPreference(true);
      return true;
    }

    return false;
  });

  useEffect(() => {
    persistPreference(visualDebuggerPreference);
  }, [visualDebuggerPreference]);

  useEffect(() => {
    if (queryOverrideActive && !visualDebuggerPreference) {
      setVisualDebuggerPreference(true);
    }
  }, [queryOverrideActive, visualDebuggerPreference]);

  const visualDebuggerEnabled = queryOverrideActive || visualDebuggerPreference;

  useEffect(() => {
    VisualLogger.setEnabled(visualDebuggerEnabled);
  }, [visualDebuggerEnabled]);

  const enableVisualDebugger = useCallback(() => {
    setVisualDebuggerPreference(true);
  }, []);

  const disableVisualDebugger = useCallback(() => {
    setVisualDebuggerPreference(false);
  }, []);

  const toggleVisualDebugger = useCallback(() => {
    setVisualDebuggerPreference((prev) => !prev);
  }, []);

  return {
    visualDebuggerEnabled,
    enableVisualDebugger,
    disableVisualDebugger,
    toggleVisualDebugger,
    setVisualDebuggerPreference,
    queryOverrideActive,
  };
};

export default useDebugPreferences;
